import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import axios from "axios";
import { Toaster } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import PortalConfig from "@/pages/PortalConfig";
import PortalView from "@/pages/PortalView";
import ClientPortal from "@/pages/ClientPortal";
import PublicPortal from "@/pages/PublicPortal";
import AcceptInvite from "@/pages/AcceptInvite";
import ResetPassword from "@/pages/ResetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminResetPassword from "@/pages/AdminResetPassword";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { completeLogin } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/login", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId }
        );

        const { access_token, token, refresh_token, user } = response.data;
        // Store JWT tokens in localStorage via completeLogin
        await completeLogin(access_token || token, user, refresh_token);

        // Clear hash and navigate to dashboard
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true });
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login", { replace: true });
      }
    };

    processSession();
  }, [navigate, completeLogin]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-foreground/60">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check both React state and localStorage (for fresh logins where state hasn't propagated yet)
  const hasToken = localStorage.getItem("auth_token");
  if (!user && !hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If we have a token but no user yet, show loading (checkAuth will run)
  if (!user && hasToken) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return children;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("auth_token"));
  const [csrfToken, setCsrfToken] = useState(null);
  const refreshTimeoutRef = useRef(null);

  // Fetch CSRF token when authenticated
  const fetchCsrfToken = async (authToken) => {
    try {
      const response = await axios.get(`${API}/csrf-token`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCsrfToken(response.data.csrf_token);
      return response.data.csrf_token;
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      return null;
    }
  };

  // Refresh access token using refresh token
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: refreshToken
      });
      const newAccessToken = response.data.access_token;
      localStorage.setItem("auth_token", newAccessToken);
      setToken(newAccessToken);
      
      // Schedule next refresh (5 minutes before expiry)
      const expiresIn = response.data.expires_in || 1800; // default 30 min
      scheduleTokenRefresh(expiresIn);
      
      return newAccessToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Refresh token expired or invalid - force logout
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      setToken(null);
      setUser(null);
      setCsrfToken(null);
      return null;
    }
  };

  // Schedule automatic token refresh
  const scheduleTokenRefresh = (expiresInSeconds) => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Refresh 5 minutes before expiry (or at half-time if less than 10 min)
    const refreshTime = Math.max((expiresInSeconds - 300) * 1000, expiresInSeconds * 500);
    
    refreshTimeoutRef.current = setTimeout(() => {
      refreshAccessToken();
    }, refreshTime);
  };

  useEffect(() => {
    const checkAuth = async () => {
      // First check if we have a token in localStorage
      const storedToken = localStorage.getItem("auth_token");
      
      if (!storedToken) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        setUser(response.data);
        // Fetch CSRF token after authentication
        await fetchCsrfToken(storedToken);
        // Schedule token refresh (assuming 30 min token, refresh after 25 min)
        scheduleTokenRefresh(1800);
      } catch (error) {
        // Token expired - try to refresh
        if (error.response?.status === 401) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            // Retry with new token
            try {
              const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${newToken}` }
              });
              setUser(response.data);
              await fetchCsrfToken(newToken);
            } catch (retryError) {
              setUser(null);
              localStorage.removeItem("auth_token");
              localStorage.removeItem("refresh_token");
              setToken(null);
              setCsrfToken(null);
            }
          } else {
            setUser(null);
            localStorage.removeItem("auth_token");
            setToken(null);
            setCsrfToken(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
    
    // Cleanup timeout on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // With 2FA, login now returns otp_token instead of auth token
    // The actual login completion happens in completeLogin
    return response.data;
  };

  const completeLogin = async (authToken, userData, refreshToken = null) => {
    localStorage.setItem("auth_token", authToken);
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
    setToken(authToken);
    setUser(userData);
    setLoading(false);
    // Fetch CSRF token after successful login
    await fetchCsrfToken(authToken);
    // Schedule token refresh
    scheduleTokenRefresh(1800); // 30 minutes
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: authToken, user: userData } = response.data;
    localStorage.setItem("auth_token", authToken);
    setToken(authToken);
    setUser(userData);
    // Fetch CSRF token after registration
    await fetchCsrfToken(authToken);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    // Clear refresh timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    try {
      await axios.post(`${API}/auth/logout`, {}, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    setToken(null);
    setUser(null);
    setCsrfToken(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, completeLogin, register, loginWithGoogle, logout, token, csrfToken, refreshAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/:portalId"
        element={
          <ProtectedRoute>
            <PortalView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/:portalId/config"
        element={
          <ProtectedRoute>
            <PortalConfig />
          </ProtectedRoute>
        }
      />
      <Route path="/forgot-password" element={<AdminForgotPassword />} />
      <Route path="/admin-reset-password/:token" element={<AdminResetPassword />} />
      <Route path="/p/:shareToken" element={<PublicPortal />} />
      <Route path="/p/:shareToken/forgot-password" element={<ForgotPassword />} />
      <Route path="/c/:accessToken" element={<ClientPortal />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <div className="App min-h-screen bg-background">
        <div className="noise-overlay" />
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--foreground))',
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
